import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, ChevronDown, ChevronUp, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtRp, fmtDate } from "@/lib/api";
import { JournalForm } from "./JournalForm";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export function JournalTab() {
  const [list, setList] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [filters, setFilters] = useState({ search: "", source: "all", start: "", end: "" });
  const [form, setForm] = useState({ open: false, item: null });
  const [expanded, setExpanded] = useState({});

  const load = useCallback(() => {
    const params = { ...(filters.search && { search: filters.search }), ...(filters.source !== "all" && { source: filters.source }), ...(filters.start && { start: filters.start }), ...(filters.end && { end: filters.end }) };
    api.get("/accounting/journals", { params }).then((r) => setList(r.data));
  }, [filters]);

  useEffect(() => { api.get("/accounting/accounts").then((r) => setAccounts(r.data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const remove = async (j) => {
    if (!window.confirm(`Hapus jurnal ${j.entry_no}?`)) return;
    try { await api.delete(`/accounting/journals/${j.id}`); toast.success("Jurnal dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="acc-journal">
      <div className="card-aqua p-4 mb-4 flex flex-col md:flex-row gap-3 md:items-center rise rise-1">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Cari deskripsi, no. jurnal, akun..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="pl-9" data-testid="input-search-journal" />
        </div>
        <Select value={filters.source} onValueChange={(v) => setFilters({ ...filters, source: v })}>
          <SelectTrigger className="w-44" data-testid="select-journal-source"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua sumber</SelectItem><SelectItem value="manual">Manual</SelectItem><SelectItem value="stock">Otomatis Stok</SelectItem><SelectItem value="payment">Pembayaran Masuk</SelectItem><SelectItem value="expense">Pengeluaran</SelectItem><SelectItem value="income">Pendapatan Lain</SelectItem><SelectItem value="closing">Penutup Tahun</SelectItem></SelectContent>
        </Select>
        <Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="w-40" data-testid="input-journal-start" />
        <Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="w-40" data-testid="input-journal-end" />
        <Button className="rounded-full bg-sky-600 hover:bg-sky-700" onClick={() => setForm({ open: true, item: null })} data-testid="btn-add-journal"><Plus className="h-4 w-4 mr-1" /> Entri Jurnal</Button>
      </div>

      <div className="card-aqua overflow-hidden rise rise-2">
        <table className="w-full text-sm" data-testid="table-journals">
          <thead className="table-head"><tr><th className="w-8" /><th className="text-left px-4 py-3">Tanggal</th><th className="text-left px-4 py-3">No. Jurnal</th><th className="text-left px-4 py-3">Deskripsi</th><th className="text-left px-4 py-3">Ref</th><th className="text-left px-4 py-3">Sumber</th><th className="text-right px-4 py-3">Total (Rp)</th><th className="text-right px-4 py-3">Aksi</th></tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-slate-500" data-testid="journals-empty">Belum ada jurnal.</td></tr>}
            {list.map((j, idx) => (
              <JournalRow key={j.id} j={j} idx={idx} open={!!expanded[j.id]} onToggle={() => setExpanded({ ...expanded, [j.id]: !expanded[j.id] })}
                onEdit={() => setForm({ open: true, item: j })} onDelete={() => remove(j)} />
            ))}
          </tbody>
        </table>
      </div>
      <JournalForm open={form.open} item={form.item} onClose={() => setForm({ open: false, item: null })} onSaved={load} accounts={accounts} />
    </div>
  );
}

function JournalRow({ j, idx, open, onToggle, onEdit, onDelete }) {
  return (
    <>
      <tr className="border-t border-sky-50 hover:bg-sky-50/50 cursor-pointer" onClick={onToggle} data-testid={`journal-row-${idx}`}>
        <td className="pl-3 text-slate-400">{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</td>
        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(j.date)}</td>
        <td className="px-4 py-3 font-mono text-xs font-semibold text-sky-800">{j.entry_no}{j.locked && <Lock className="inline h-3 w-3 ml-1 text-slate-400" data-testid={`journal-locked-${idx}`} />}</td>
        <td className="px-4 py-3 text-slate-800">{j.description}</td>
        <td className="px-4 py-3 text-slate-500 font-mono text-xs">{j.reference || "—"}</td>
        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${{ stock: "bg-sky-50 text-sky-700", payment: "bg-emerald-50 text-emerald-700", expense: "bg-rose-50 text-rose-700", income: "bg-teal-50 text-teal-700", closing: "bg-violet-50 text-violet-700" }[j.source] || "bg-slate-100 text-slate-600"}`}>{{ stock: "Otomatis Stok", payment: "Pembayaran", expense: "Pengeluaran", income: "Pendapatan Lain", closing: "Penutup Tahun" }[j.source] || "Manual"}</span></td>
        <td className="px-4 py-3 text-right font-mono font-bold">{fmtRp(j.total)}</td>
        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
          {!j.locked && <div className="inline-flex gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8 text-sky-700" onClick={onEdit} data-testid={`btn-edit-journal-${idx}`}><Pencil className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={onDelete} data-testid={`btn-delete-journal-${idx}`}><Trash2 className="h-4 w-4" /></Button>
          </div>}
        </td>
      </tr>
      {open && (
        <tr className="bg-sky-50/40" data-testid={`journal-lines-${idx}`}>
          <td />
          <td colSpan={7} className="px-4 py-2">
            <table className="w-full text-xs">
              <tbody>
                {j.lines.map((l, i) => (
                  <tr key={i} className="border-b border-sky-100 last:border-0">
                    <td className={`py-1.5 ${l.credit > 0 ? "pl-8" : ""} text-slate-700`}><span className="font-mono text-slate-400 mr-2">{l.account_code}</span>{l.account_name}</td>
                    <td className="py-1.5 text-right font-mono w-40">{l.debit > 0 ? fmtRp(l.debit) : ""}</td>
                    <td className="py-1.5 text-right font-mono w-40">{l.credit > 0 ? fmtRp(l.credit) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-[11px] text-slate-400 mt-1">Dibuat oleh {j.created_by}</div>
          </td>
        </tr>
      )}
    </>
  );
}
